import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import type { OverlayRef } from '@angular/cdk/overlay';

export const POPOVER_DEFAULT_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
  },
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -8,
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 8,
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -8,
  },
];

@Component({
  selector: 'app-popover',
  standalone: true,
  imports: [CdkConnectedOverlay],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './popover.component.html',
})
export class PopoverComponent {
  readonly origin = input.required<CdkOverlayOrigin>();
  readonly open = input(false);
  readonly title = input<string>();
  readonly panelClass = input('');
  readonly positions = input<ConnectedPosition[]>(POPOVER_DEFAULT_POSITIONS);
  readonly repositionOnResize = input(false);
  readonly closed = output<void>();

  private resizeObserver: ResizeObserver | null = null;
  private repositionScheduled = false;
  private repositionFrame = 0;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopRepositioning());
  }

  protected onOverlayAttach(overlayRef: OverlayRef): void {
    if (!this.repositionOnResize() || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() =>
      this.scheduleReposition(overlayRef)
    );
    this.resizeObserver.observe(overlayRef.overlayElement);
  }

  protected onOverlayDetach(): void {
    this.stopRepositioning();
    this.closed.emit();
  }

  private scheduleReposition(overlayRef: OverlayRef): void {
    if (this.repositionScheduled) {
      return;
    }
    this.repositionScheduled = true;
    this.repositionFrame = requestAnimationFrame(() => {
      this.repositionScheduled = false;
      overlayRef.updatePosition();
    });
  }

  private stopRepositioning(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.repositionScheduled) {
      cancelAnimationFrame(this.repositionFrame);
      this.repositionScheduled = false;
    }
  }
}
